import {
    AttachedFiles,
    type AttachedFilesRequest,
    type BTFileSystem,
    BTServiceFileUpload,
    type IHasAttachedFiles,
} from "legacyComponents/FileUploadContainer.types";
import {
    type IHasLineItems,
    type ILineItemContainerData,
    type ILineItemData,
    type ILineItemResponse,
    LineItem,
    LineItemType,
    LinkedCostItemTypes,
    PriceTypes,
} from "legacyComponents/LineItemContainer.types";
import { uniqBy } from "lodash-es";
import moment from "moment";

import type { LineItem as HandlerLineItem } from "@btnet/services-btnet/handlers/models/lineItem";
import { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";

import {
    BTSelectItem,
    type IBaseEntity,
    mapBTServiceDropdownToSelectItem,
} from "types/apiResponse/apiResponse";
import {
    type CostTypes,
    DocumentInstanceType,
    MarkedAs,
    RequireSelectionSignaturePrefs,
} from "types/enum";

import { getReadOnly } from "utilities/filters/filter";
import { getSelectedValue } from "utilities/form/form";
import {
    calculateBuilderCost,
    calculateMarginPercentageFromMarkup,
} from "utilities/lineItem/lineItemHelper";

import {
    RelatedInvoice,
    RelatedPurchaseOrder,
} from "commonComponents/entity/relatedItem/RelatedItem.types";
import { type IHasApprovalComments } from "commonComponents/utilities/ApprovalComments/ApprovalComments";
import { type ILineItemRow } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";

import { type ILineItemRelatedItem } from "entity/hawk/api/lineItem.api.types";
import { ChoiceStatus } from "entity/selectionChoice/common/SelectionChoiceStatus/SelectionChoiceStatus";
import {
    type ISelectionChoiceLineItem,
    type ISelectionOptionLineItem,
} from "entity/selectionChoice/SelectionChoiceLineItemContainer/SelectionChoiceLineItemContainer.types";
import { type ISelectionOptionFormValues } from "entity/selectionOptions/SelectionOption/SelectionOptionPresentational";
import { type TaxMethod } from "entity/tax/common/tax.types";
import { TaxRateBreakdownInfo } from "entity/tax/common/TaxRateBreakdown/TaxRateBreakdown.api.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";
import { type VendorTypes } from "entity/vendor/common/vendor.types";
import { type ICanSendToCartEntity } from "entity/vendor/common/vendor.types";

export type SelectionChoiceFormActions =
    | undefined
    | "save"
    | "delete"
    | "saveAndNew"
    | "approve"
    | "decline"
    | "copy"
    | "favorite"
    | "resetToPending"
    | "newInvoice"
    | "requestPrice"
    | "addToPO"
    | "sendToHomeDepot"
    | "resetPriceRequested"
    | "checkedActions"
    | "saveAndClose";

export enum PriceRequestedStatus {
    NotRequested = 1,
    VendorRequested = 2,
    VendorReturned = 3,
}

export interface ISelectionChoiceFormValues extends IHasApprovalComments {
    title: string;
    productLink: string;
    includeInBudget: boolean;
    includeInProposal: boolean;
    vendorId: number | null;
    installerId: number | null;
    description: string;
    lineItems?: LineItem[];
    priceType?: PriceTypes;
    containerIsValid: boolean;
    amountInvoiced?: number;
    builderPrice?: number;
    ownerPrice?: number;
    isTBD: boolean;
    subPriceNotes?: string;
    attachedFiles: AttachedFilesRequest;
    attachedFilesConfiguration: AttachedFiles;
    showLineItemsToOwner: boolean;
    notifyOwner?: boolean;
    approveChoice?: boolean;
    signature?: BTFileSystem;
    comments?: string;
    selectionChoiceLineItems: ISelectionChoiceLineItem[];
    taxMethod: TaxMethod;
    taxGroupId?: number;
    showTax?: boolean;
}

const getPriceType = (data: any): PriceTypes => {
    if (
        data.vendorPriceRequestedStatus &&
        data.vendorPriceRequestedStatus === PriceRequestedStatus.VendorRequested
    ) {
        return PriceTypes.RequestFromVendor;
    } else {
        return data.costFormat
            ? (getSelectedValue(
                  data.costFormat.value.map((cf: any) => new BTSelectItem(cf)),
              ) as PriceTypes)
            : PriceTypes.LineItems;
    }
};

const getLineItemContainerData = (data: any): ILineItemContainerData => {
    const status: ChoiceStatus = data.status.value;
    return {
        pageType: "selection-choice",
        pageTypeEnum: LineItemType.SelectionChoice,
        priceType: getPriceType(data),
        lineItemReadOnly: getReadOnly(data.lineItems.validators),
        hideRadioButtons: false,
        showCatalog: true,
        displayTitle: true,
        canReorderLineItems: true,
        descriptionMax: 1200,
        showTbd: [ChoiceStatus.Unreleased, ChoiceStatus.Pending, ChoiceStatus.Unavailable].includes(
            status,
        ),
        showLineItemOnFlatFee: false,
        showToOwner: data.showLineItemsToOwner ? data.showLineItemsToOwner.value : false,
        showToOwnerCheckBox: data.showLineItemsToOwner !== undefined,
        showRequestFromVendor: true,
        isOwnerApproved: data.status.value === ChoiceStatus.Selected,
        onlyLockMarkup: [ChoiceStatus.Selected, ChoiceStatus.DeclinedByOwner].includes(status),
        hasEditPermission: data.canEdit,
        subDescription: data.subPriceComments,
        inactiveCostCodeCutoffDate: data.inactiveCostCodeCutoffDate,
        hasPercentInvoicePermission: data.canViewOwnerInvoice,
        canAddOwnerPayments: data.canAddOwnerInvoice,
        tbdReadOnly: [
            ChoiceStatus.DeclinedByOwner,
            ChoiceStatus.DeclinedByBuilder,
            ChoiceStatus.BuilderOverride,
            ChoiceStatus.Selected,
        ].includes(status),
        priceRequested: data.vendorPriceRequestedDateDisplay,
        showMarkupTotal: true,
        vendorID: data.vendor
            ? getSelectedValue(data.vendor.value.map((v: any) => new BTSelectItem(v)))
            : -1,
        entityId: data.id,
        jobId: data.jobId,
        linkedCostItemType: LinkedCostItemTypes.SelectionChoice,
        canEditCostCodes: data.lineItems.canEditCostCodes,
    };
};
export class SelectionChoiceEntity
    implements IHasLineItems, IHasAttachedFiles, IBaseEntity, ICanSendToCartEntity
{
    constructor(data: any) {
        this.id = data.id;
        this.builderId = data.builderId;
        this.jobId = data.jobId;
        this.isTemplate = data.jobInfo?.isStandaloneTemplate ?? false;
        this.selectionId = data.selectionId;
        this.title = data.title.value;
        this.isTitleReadOnly = getReadOnly(data.title.validators);
        this.parentSelection = data.parentSelection.value;
        this.category = data.parentCategory.value;
        this.location = data.parentLocation.value;
        this.dependentSelectionsCount = data.dependentCount;
        this.status = data.status.value;
        this.statusMessage = data.status.message;
        this.showBudgetedChoice = data.budgetedChoice !== undefined;
        this.budgetedChoice = data.budgetedChoice !== undefined ? data.budgetedChoice.value : false;
        this.showIncludeInProposal = data.includeInProposal !== undefined;
        this.includeInProposal =
            data.includeInProposal !== undefined ? data.includeInProposal.value : true;
        this.productLink = data.productLink.value;
        this.addedBy = data.addedBy;
        this.addedById = data.addedById;
        this.addedOn = moment(data.addedOn);
        this.subPriceComments = data.subPriceComments ? data.subPriceComments.value : undefined;
        this.builderCost = data.builderPrice ? data.builderPrice.value : undefined;
        this.createdByCurrentUser = data.createdByCurrentUser;
        this.vendors = data.vendor
            ? data.vendor.value.map((v: any) => new BTSelectItem(v))
            : undefined;
        this.isVendorReadOnly = data.vendor ? getReadOnly(data.vendor.validators) : true;
        this.installers = data.installers
            ? data.installers.value.map((i: any) => new BTSelectItem(i))
            : undefined;
        this.isVendor = data.isVendor || false;
        this.vendorResetMessage = data.vendorResetMessage;
        this.vendorPriceRequestedStatus = data.vendorPriceRequestedStatus;
        this.isBuilderPriceRequested = data.isBuilderPriceRequested || false;
        this.builderPriceRequestedDate = data.builderPriceRequested
            ? moment(data.builderPriceRequested.value)
            : data.priceRequestedOn
              ? moment(data.priceRequestedOn.value)
              : undefined;
        this.approvalDetails = data.approvalDetails
            ? new ApprovalDetails(data.approvalDetails)
            : undefined;
        this.approvalMessage = data.approvalMessage;
        this.disclaimerMessage = data.disclaimerMessage;
        this.description = data.details ? data.details.value : data.description.value;
        this.isDescriptionReadOnly = data.details
            ? getReadOnly(data.details.validators)
            : getReadOnly(data.description.validators);
        this.ownerPriceTBD = data.priceTBD ? data.priceTBD.value : data.isOwnerPriceTbd || false;
        this.rawOwnerPrice = data.rawOwnerPrice && data.rawOwnerPrice.value;
        this.ownerPrice = this.ownerPriceTBD
            ? data.ownerPrice !== undefined
                ? 0
                : undefined
            : data.ownerPrice
              ? data.ownerPrice.value
              : undefined;
        this.relatedInvoices = data.relatedInvoices
            ? data.relatedInvoices.map((x: any) => new RelatedInvoice(x))
            : [];
        this.relatedPurchaseOrders = data.relatedPOs
            ? uniqBy(
                  data.relatedPOs.map((x: any) => new RelatedPurchaseOrder(x)),
                  (po) => po.id,
              )
            : [];
        this.canAdd = data.canAdd;
        this.canAddOwnerInvoice = data.canAddOwnerInvoice;
        this.canEditOwnerInvoice = data.canEditOwnerInvoice;
        this.hasExistingInvoices = data.hasExistingInvoices;
        this.canViewOwnerInvoice = data.canViewOwnerInvoice;
        this.canAddPurchaseOrder = data.canAddPurchaseOrder || false;
        this.canUpdatePurchaseOrder = data.canUpdatePurchaseOrder || false;
        this.canEdit = data.canEdit;
        this.canDelete = data.canDelete;
        this.canFavorite = data.canFavorite;
        this.canApprove = data.canApprove;
        this.canDecline = data.canDecline;
        this.canViewPrices = data.ownerPrice !== undefined || data.builderPrice !== undefined;
        this.canSendToHomeDepotCart = data.canSendToHomeDepotCart;
        this.isFavorited = data.favorited;
        this.documentInstanceType = DocumentInstanceType.SelectionChoice;
        this.attachedFiles = new AttachedFiles(data.attachedFiles);
        this.name = "Selection Choice";
        this.showLineItemsToOwner = data.showLineItemsToOwner
            ? data.showLineItemsToOwner.value
            : false;
        this.signaturePrefs = data.signaturePrefs || RequireSelectionSignaturePrefs.None;
        this.parentSelectionIsMultipleChoice = data.parentSelectionIsMultipleChoice;
        this.showNotifyOwnerPrompt = data.showNotifyOwnerPrompt;
        this.showApproveChoicePrompt = data.showApproveChoicePrompt;
        if (data.lineItems) {
            this.lineItems = data.lineItems;
            this.lineItemContainerData = getLineItemContainerData(data);
            this.lineItemInitialValues = data.lineItems.value
                ? data.lineItems.value.map(
                      (li: ILineItemResponse) =>
                          new LineItem(
                              li,
                              this.lineItemContainerData.pageType,
                              this.lineItemContainerData.pageTypeEnum,
                          ),
                  )
                : [];
        } else {
            this.lineItemInitialValues = [];
        }

        this.selectionChoiceLineItems = data.lineItems?.value.map(
            (x: ILineItemResponse) => new SelectionChoiceLineItem(x) ?? [],
        );

        this.taxMethods = data.taxMethod && mapBTServiceDropdownToSelectItem(data.taxMethod);

        this.taxGroupId = data.taxGroupId;
        this.taxRateBreakdown =
            data.taxRateBreakdown && new TaxRateBreakdownInfo(data.taxRateBreakdown);

        this.invoicedAllowanceAmount = data.invoicedAllowanceAmount;
        this.invoicedAllowanceCustomInvoiceId = data.invoicedAllowanceCustomInvoiceId;
        this.hasCostCodesFeature = data.hasCostCodesFeature;
    }

    id: number;
    builderId: number;
    jobId: number;
    isTemplate: boolean;
    selectionId: number;
    title: string;
    name: string;
    parentSelection: string;
    category: string;
    location: string;
    dependentSelectionsCount: number;
    status: ChoiceStatus;
    budgetedChoice: boolean;
    showBudgetedChoice: boolean;
    includeInProposal: boolean;
    showIncludeInProposal: boolean;
    productLink: string;
    description: string;
    isDescriptionReadOnly: boolean;
    addedBy: string;
    addedById: number;
    addedOn: moment.Moment;
    builderCost?: number;
    rawOwnerPrice?: number;
    ownerPrice?: number | string;
    ownerPriceTBD: boolean;
    subPriceComments?: string;
    createdByCurrentUser: boolean;
    statusMessage: string;
    vendors?: BTSelectItem[];
    isVendorReadOnly: boolean;
    isTitleReadOnly: boolean;
    installers?: BTSelectItem[];
    isVendor: boolean;
    vendorPriceRequestedStatus: PriceRequestedStatus;
    vendorResetMessage: string;
    isBuilderPriceRequested: boolean;
    builderPriceRequestedDate?: moment.Moment;
    approvalDetails?: ApprovalDetails;
    approvalMessage?: string;
    disclaimerMessage?: string;
    lineItems: ILineItemData;
    lineItemContainerData: ILineItemContainerData;
    lineItemInitialValues: LineItem[];
    relatedInvoices: RelatedInvoice[];
    relatedPurchaseOrders: RelatedPurchaseOrder[];
    canAdd: boolean;
    canAddOwnerInvoice: boolean;
    canEditOwnerInvoice: boolean;
    hasExistingInvoices: boolean;
    canViewOwnerInvoice: boolean;
    canAddPurchaseOrder: boolean;
    canUpdatePurchaseOrder: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canFavorite: boolean;
    canApprove: boolean;
    canDecline: boolean;
    canViewPrices: boolean;
    canSendToHomeDepotCart: boolean;
    isFavorited: boolean;
    documentInstanceType: DocumentInstanceType;
    attachedFiles: AttachedFiles;
    showLineItemsToOwner: boolean;
    signaturePrefs?: RequireSelectionSignaturePrefs;
    parentSelectionIsMultipleChoice: boolean;
    showNotifyOwnerPrompt: boolean;
    showApproveChoicePrompt: boolean;
    selectionChoiceLineItems: SelectionChoiceLineItem[];
    taxMethods: BTSelectItem[] | undefined;
    taxGroupId?: number;
    taxRateBreakdown: TaxRateBreakdownInfo;
    invoicedAllowanceAmount?: number;
    invoicedAllowanceCustomInvoiceId?: string;
    hasCostCodesFeature: boolean;
}

export class ApprovalDetails {
    constructor(data: any) {
        this.signature = data.signature;
        this.comments = data.comments?.value;
        this.status = data.status;
        this.statusText = data.statusText;
    }
    signature?: string;
    comments: string;
    status?: ChoiceStatus;
    statusText?: string;
}

export class SelectionChoiceDeleteResponse {}

export class SelectionChoiceSaveRequest {
    constructor(formValues: ISelectionChoiceFormValues) {
        this.title = formValues.title;
        this.vendor = formValues.vendorId;
        this.installers = formValues.installerId;
        this.productLink = formValues.productLink;
        this.details = formValues.description;
        this.attachedFiles = formValues.attachedFiles;
        this.showLineItemsToOwner = formValues.showLineItemsToOwner;
        this.budgetedChoice = formValues.includeInBudget;
        this.includeInProposal = formValues.includeInProposal;
        this.builderPrice = formValues.builderPrice;
        this.ownerPrice = formValues.ownerPrice;
        this.priceTBD = formValues.isTBD;
        this.notifyOwner = formValues.notifyOwner;
        this.approveChoice = formValues.approveChoice;
        this.comments = formValues.comments;
        this.subPriceComments = formValues.subPriceNotes;
        this.taxMethod = formValues.taxMethod;
        if (formValues.signature) {
            this.updateSignature = new BTServiceFileUpload({
                ext: formValues.signature.extension,
                fileName: formValues.signature.title,
                imagePath: formValues.signature.docPath,
                tempId: formValues.signature.id,
                url: formValues.signature.docPath,
            });
        }
        if (formValues.lineItems) {
            this.lineItems = {
                updated: formValues.lineItems,
            };
        }
        if (formValues.priceType) {
            this.costFormat = formValues.priceType;
        }
        this.taxGroupId = formValues.taxGroupId;
    }

    title: string;
    vendor: number | null;
    installers: number | null;
    productLink: string;
    details: string;
    attachedFiles: AttachedFilesRequest;
    lineItems: {
        updated: LineItem[];
    };
    costFormat: PriceTypes;
    showLineItemsToOwner: boolean;
    budgetedChoice: boolean;
    includeInProposal: boolean;
    builderPrice?: number;
    ownerPrice?: number;
    priceTBD?: boolean;
    comments?: string;
    notifyOwner?: boolean;
    approveChoice?: boolean;
    updateSignature?: BTServiceFileUpload;
    subPriceComments?: string;
    taxMethod: TaxMethod;
    taxGroupId?: number;
}

export class SelectionChoiceSaveResponse {
    constructor(data: any) {
        this.id = data.id;
        this.jobId = data.jobId;
        this.selectionId = data.selectionId;
    }
    id: number;
    jobId: number;
    selectionId: number;
}

export class SelectionChoiceFavoriteResponse {}
export interface ISelectionChoiceFavoriteRequest {
    favoriting: boolean;
}

export interface ISelectionChoiceResetRequest {
    updateCostItems: boolean | null;
}

export class SelectionChoiceResetResponse {}

export class DependentSelectionsResponse {
    constructor(data: any) {
        this.selections = data.selections.map((s: any) => new DependentSelection(s));
    }
    selections: DependentSelection[];
}

export class DependentSelection {
    constructor(data: any) {
        this.title = data.title;
        this.status = data.status;
        this.selectionId = data.selectionId;
        this.choices = data.choices.map((c: any) => new DependentSelectionChoice(c));
    }
    title: string;
    status: ChoiceStatus;
    selectionId: number;
    choices: DependentSelectionChoice[];
}

export class DependentSelectionChoice {
    constructor(data: any) {
        this.title = data.title;
        this.choiceId = data.choiceId;
        this.selectionId = data.selectionId;
        this.status = data.status;
    }
    title: string;
    choiceId: number;
    selectionId: number;
    status: ChoiceStatus;
}

/* Choice Cards */
export type SelectionChoiceCardFormActions =
    | undefined
    | "approve"
    | "decline"
    | "favorite"
    | "approveAll";

export class SelectionChoiceCardResponse {
    constructor(data: any) {
        this.selectionChoices = data.selectionChoices.map(
            (sc: any) => new SelectionChoiceCardEntity(sc),
        );
    }
    selectionChoices: SelectionChoiceCardEntity[];
}

export class SelectionChoiceCardEntity {
    constructor(data: any) {
        this.id = data.ID;
        this.jobId = data.jobId;
        this.title = data.Title;
        this.description = data.description;
        this.builderPrice = data.BuilderPrice;
        this.ownerPrice = data.OwnerPrice;
        this.status = data.statusId;
        this.statusMessage = data.statusMessageWeb;
        this.addedByIcon = data.addedByIcon;
        this.addedByName = data.addedByName;
        this.showApproveButton = data.showApproveButton;
        this.showDeclineButton = data.showDeclineButton;
        this.canFavorite = data.canFavorite;
        this.isFavorited = data.isFavorited;
        this.imageUrl = data.ImageUrl;
        this.fileCount = data.fileCount;
        this.approvalMessage = data.approvalMessage;
        this.autoSortOrder = data.autoSortOrder;
    }
    id: number;
    jobId: number;
    title: string;
    description: string;
    builderPrice: string;
    ownerPrice: string;
    status: ChoiceStatus;
    statusMessage: string;
    addedByIcon: string;
    addedByName: string;
    showApproveButton: boolean;
    showDeclineButton: boolean;
    canFavorite: boolean;
    isFavorited: boolean;
    imageUrl?: string;
    fileCount: number;
    approvalMessage?: string;
    autoSortOrder: number;
}

export interface ISelectionChoiceUpdateAllRequest {
    approving: boolean;
    updateSignature?: BTServiceFileUpload;
}

export class SelectionChoiceUpdateAllResponse {}

export class SelectionChoiceDeleteAllResponse {
    constructor(data: any) {
        this.errorList = data.errorList;
        this.count = data.count;
    }
    errorList: string[];
    count: number;
}

export class RequestBuilderPriceResponse {
    constructor(data: any) {
        this.isBuilderPriceRequested = data.isBuilderPriceRequested;
        this.builderPriceRequestedDate = moment(data.builderPriceRequestedDate.value);
        this.status = data.status.value;
        this.statusMessage = data.status.message;
    }
    isBuilderPriceRequested: boolean;
    builderPriceRequestedDate: moment.Moment;
    status: ChoiceStatus;
    statusMessage: string;
}

export interface ISelectionOptionLineItemData {
    amountInvoiced?: number | null;
    autoFocusedField?: string | null;
    costCode?: number | null;
    costCodeId?: number | null;
    costCodeItemId?: number | null;
    costCodeTitle?: string | null;
    costItemId?: number | null;
    costTypes?: CostTypes[] | null;
    description?: string | null;
    id?: number | null;
    internalNotes?: string | null;
    isExpanded?: boolean | null;
    _isInEdit?: boolean | null;
    isTBD?: boolean | null;
    itemTitle?: string | null;
    lineItemId?: number | null;
    markedAs?: MarkedAs | null;
    markupAmount?: number | null;
    markupColumn?: MarkupColumnTypes | 0 | null;
    markupPercent?: number | null;
    markupPercentage?: number | null;
    markupPerUnit?: number | null;
    markupPrice?: number | null;
    markupType?: MarkupColumnTypes | null;
    ownerPrice?: number | null;
    quantity?: number | null;
    relatedItems?: ILineItemRelatedItem[] | null;
    taxGroupId?: number | null;
    title?: string | null;
    totalWithTax?: number | null;
    unit?: string | null;
    unitCost?: number | null;
    unitType?: string | null;
}

export class SelectionOptionLineItem implements ISelectionOptionLineItem, ILineItemRow {
    constructor(data: ISelectionOptionLineItemData) {
        this.id = data.lineItemId ?? data.id ?? 0;
        this.unitCost = data.unitCost ?? 0;
        this.quantity = data.quantity ?? 0;
        this.builderCost = calculateBuilderCost(this.unitCost, this.quantity);
        this.unit = data.unitType ?? data.unit ?? "";
        this.ownerPrice = data.ownerPrice ?? 0;
        this.amountInvoiced = data.amountInvoiced ?? 0;
        this.markupAmount = data.markupAmount ?? data.markupPrice ?? 0;
        this.markupPerUnit = data.markupPerUnit ?? 0;
        this.markupPercent = data.markupPercent ?? data.markupPercentage ?? 0;
        // TODO: update this to not use the deprecated function - HV is sending up margin
        this.margin = calculateMarginPercentageFromMarkup(
            data.markupPercent ?? 0,
            data.markupAmount ?? 0,
            data.markupPerUnit ?? 0,
            data.unitCost ?? 0,
            data.quantity ?? 0,
        );
        this.costCodeId = data.costCode ?? data.costCodeId ?? null;
        this.itemTitle = data.title ?? data.itemTitle ?? null;
        this.costItemId = data.costCodeItemId ?? data.costItemId ?? null;
        this.lineItemType = LineItemType.SelectionChoice;
        this.isExpanded = data.isExpanded ?? false;
        this.description = data.description ?? "";
        this.internalNotes = data.internalNotes ?? null;
        this.costTypes = data.costTypes ?? [];
        this.markedAs = data.markedAs ?? MarkedAs.None;
        this.isEditable = true;
        this._isInEdit = data._isInEdit ?? undefined;
        this.isTBD = data.isTBD ?? false;
        this.costCodeTitle = data.costCodeTitle ?? null;
        this.autoFocusedField = data.autoFocusedField ?? undefined;
        this.markupType =
            data.markupType ??
            (data.markupColumn !== 0 ? data.markupColumn : undefined) ??
            MarkupColumnTypes.Percentage; // Default to Percentage
        this.taxGroupId = data.taxGroupId ?? NoTaxId;
        this.totalWithTax = data.totalWithTax ?? undefined;
        this.relatedItems = data.relatedItems ?? [];
    }

    id: number;
    unitCost: number;
    unit: string;
    quantity: number;
    builderCost: number;
    markupType: MarkupColumnTypes;
    markupPercent: number;
    markupPerUnit: number;
    markupAmount: number;
    ownerPrice: number;
    margin: number;
    amountInvoiced: number;
    description: string;
    internalNotes: string | null;
    costItemId: number | null;
    costTypes: CostTypes[] | null;
    markedAs: MarkedAs;
    parentId?: number;
    costCodeId: number | null;
    itemTitle: string | null;
    lineItemType: LineItemType;
    isTBD: boolean;
    costCodeTitle: string | null;
    relatedItems?: ILineItemRelatedItem[];
    taxGroupId?: number;
    totalWithTax?: number;

    // IGridLineItemViewRow
    _isInEdit?: boolean;
    isSelected?: boolean;
    isExpanded?: boolean;
    isEditable: boolean;
    autoFocusedField?: string;

    // format data
    formatId: number | null;
    costCategoryFormatId: number | null;
    displayOrder: number | null;
}

interface ISelectionChoiceLineItemData {
    id: number;
    lineItemId?: number | null;
    unitCost?: number | null;
    quantity?: number | null;
    unit?: string;
    unitType?: string | null;
    ownerPrice?: number | null;
    amountInvoiced?: number;
    retainageWithheld?: number | null;
    markupAmount?: number;
    markupPrice?: number | null;
    markupPerUnit?: number | null;
    markupPercent?: number;
    markupPercentage?: number | null;
    margin?: number | null;
    costCode?: number | null;
    costCodeId?: number | null;
    title?: string | null;
    itemTitle?: string | null;
    costCodeItemId?: number | null;
    costItemId?: number | null;
    isExpanded?: boolean;
    description?: string | null;
    internalNotes?: string | null;
    costTypes?: CostTypes[] | null;
    markedAs?: MarkedAs;
    _isInEdit?: boolean;
    isTBD?: boolean | null;
    costCodeTitle?: string | null;
    autoFocusedField?: string;
    markupType?: MarkupColumnTypes | null;
    markupColumn?: MarkupColumnTypes | 0 | null;
    vendorProductId?: number | null;
    vendorType?: VendorTypes | null;
    isDiscontinued?: boolean | null;
    taxGroupId?: number | null;
    totalWithTax?: number | null;
}

export class SelectionChoiceLineItem implements ISelectionChoiceLineItem, ILineItemRow {
    constructor(data: ISelectionChoiceLineItemData) {
        this.id = data.lineItemId ?? data.id;
        this.unitCost = data.unitCost ?? 0;
        this.quantity = data.quantity ?? 0;
        this.builderCost = calculateBuilderCost(this.unitCost, this.quantity);
        this.unit = data.unitType ?? data.unit ?? "";
        this.ownerPrice = data.ownerPrice ?? 0;
        this.amountInvoiced = data.amountInvoiced ?? 0;
        this.retainageWithheld = data.retainageWithheld ?? undefined;
        this.markupAmount = data.markupAmount ?? data.markupPrice ?? 0;
        this.markupPerUnit = data.markupPerUnit ?? 0;
        this.markupPercent = data.markupPercent ?? data.markupPercentage ?? 0;
        this.margin =
            data.margin ??
            calculateMarginPercentageFromMarkup(
                data.markupPercent ?? 0,
                data.markupAmount ?? 0,
                data.markupPerUnit ?? 0,
                data.unitCost ?? 0,
                data.quantity ?? 0,
            );
        this.costCodeId = data.costCode ?? data.costCodeId ?? null;
        this.itemTitle = data.title ?? data.itemTitle ?? null;
        this.costItemId = data.costCodeItemId ?? data.costItemId ?? null;
        this.lineItemType = LineItemType.SelectionChoice;
        this.isExpanded = data.isExpanded ?? false;
        this.description = data.description ?? "";
        this.internalNotes = data.internalNotes ?? null;
        this.costTypes = data.costTypes ?? [];
        this.markedAs = data.markedAs ?? MarkedAs.None;
        this.isEditable = true;
        this._isInEdit = data._isInEdit;
        this.isTBD = data.isTBD ?? false;
        this.costCodeTitle = data.costCodeTitle ?? null;
        this.autoFocusedField = data.autoFocusedField;
        this.markupType =
            data.markupType ?? (data.markupColumn || undefined) ?? MarkupColumnTypes.Percentage;
        this.vendorProductId = data.vendorProductId ?? undefined;
        this.vendorType = data.vendorType ?? undefined;
        this.isDiscontinued = data.isDiscontinued ?? undefined;
        this.taxGroupId = data.taxGroupId ?? NoTaxId;
        this.totalWithTax = data.totalWithTax ?? 0;
    }

    id: number;
    unitCost: number;
    unit: string;
    quantity: number;
    builderCost: number;
    markupType: MarkupColumnTypes;
    markupPercent: number;
    markupPerUnit: number;
    markupAmount: number;
    ownerPrice: number;
    margin: number;
    amountInvoiced: number;
    retainageWithheld?: number;
    description: string;
    internalNotes: string | null;
    costItemId: number | null;
    costTypes: CostTypes[] | null;
    markedAs: MarkedAs;
    parentId?: number | undefined;
    costCodeId: number | null;
    itemTitle: string | null;
    lineItemType: LineItemType;
    isTBD: boolean;
    costCodeTitle: string | null;
    taxGroupId?: number;
    totalWithTax?: number;

    // IVendorProductLineItem
    vendorProductId?: number;
    vendorType?: VendorTypes;
    isDiscontinued?: boolean;

    // IGridLineItemViewRow
    _isInEdit?: boolean;
    isSelected?: boolean;
    isExpanded?: boolean;
    isEditable: boolean;
    autoFocusedField?: string;

    // format data
    formatId: number | null;
    costCategoryFormatId: number | null;
    displayOrder: number | null;
}

export const transformSelectionChoiceLineItemsToLineItems = (
    newValues: ISelectionChoiceFormValues,
) => {
    return newValues.selectionChoiceLineItems.map(
        (x: ISelectionChoiceLineItem) =>
            new LineItem(
                {
                    id: x.id > 0 ? x.id : 0,
                    costCodeId: x.costCodeId,
                    costCode: x.costCodeId!,
                    costCodeItemId: x.costItemId,
                    catalogItemId: x.costItemId,
                    costTypeId: 0,
                    calculatedAmount: x.builderCost,
                    unitCost: x.unitCost,
                    quantity: x.quantity,
                    unitType: x.unit,
                    markupColumn: x.markupType as number,
                    markupPercentage: x.markupPercent,
                    margin: x.margin,
                    markupPerUnit: x.markupPerUnit,
                    markupPrice: x.markupAmount,
                    ownerPrice: x.ownerPrice,
                    title: x.itemTitle,
                    description: x.description,
                    internalNotes: x.internalNotes,
                    costTypes: x.costTypes,
                    markedAs: x.markedAs,
                    relatedGeneralItemId: x.relatedGeneralItemId,
                    isTBD: x.isTBD,
                    vendorType: x.vendorType,
                    vendorProductId: x.vendorProductId,
                    isDiscontinued: x.isDiscontinued,
                    taxGroupId: x.taxGroupId,
                } as ILineItemResponse,
                "",
                LineItemType.SelectionChoice,
            ),
    );
};

export const transformSelectionOptionLineItemsToLineItems = (
    newValues: ISelectionOptionFormValues,
): HandlerLineItem[] => {
    return (newValues.selectionOptionLineItems ?? []).map((x: ISelectionOptionLineItem) => ({
        id: x.id > 0 ? x.id : 0,
        builderCost: x.builderCost,
        costCodeId: x.costCodeId,
        costCode: x.costCodeId ?? undefined,
        costCodeItemId: x.costItemId ?? null,
        catalogItemId: x.costItemId ?? null,
        costTypeId: "0",
        calculatedAmount: x.builderCost,
        unitCost: x.unitCost,
        quantity: x.quantity,
        unitType: x.unit,
        markupColumn: x.markupType,
        markupPercentage: x.markupPercent,
        margin: x.margin,
        markupPerUnit: x.markupPerUnit,
        markupPrice: x.markupAmount,
        ownerPrice: x.ownerPrice,
        title: x.itemTitle,
        description: x.description,
        internalNotes: x.internalNotes,
        costTypes: x.costTypes ?? [],
        markedAs: x.markedAs,
        taxGroupId: x.taxGroupId ?? null,
        totalWithTax: x.totalWithTax ?? null,
    }));
};
